import React, { useEffect } from 'react';
import { useZodForm } from '../../hooks';
import { copyModalSchema, CopyModalFormData } from '../../schemas';
import { Input } from '../ui';
import Button from '../ui/Button';

interface CopyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newName: string) => void;
  originalName: string;
  itemType: 'workflow' | 'dashboard' | 'widget';
  isCopying?: boolean;
}

const CopyModal: React.FC<CopyModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  originalName,
  itemType,
  isCopying = false,
}) => {
  const form = useZodForm<CopyModalFormData>(copyModalSchema, {
    defaultValues: { newName: '' },
    mode: 'onBlur',
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  useEffect(() => {
    if (isOpen) {
      reset({ newName: `${originalName} (Copy)` });
    }
  }, [isOpen, originalName, reset]);

  const onValid = (data: CopyModalFormData) => {
    onConfirm(data.newName);
  };

  const handleClose = () => {
    reset({ newName: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={handleClose} />
      <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-primary-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Copy {itemType}</h3>
            <p className="text-sm text-neutral-500">Create a duplicate with a new name</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onValid)}>
          <div className="mb-6">
            <Input
              {...register('newName')}
              label={`New ${itemType} name`}
              required
              placeholder={`Enter name for the new ${itemType}`}
              error={!!errors.newName}
              errorMessage={errors.newName?.message}
              autoFocus
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={isCopying}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!isValid || isCopying}
              loading={isCopying}
            >
              Create Copy
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CopyModal;
